import { BodyCompositionType, Gender } from './enums';
import { TrainingLevel } from './data-model/training-level';

export interface BodyComposition {
  type: BodyCompositionType;
  maleBF_pc: number;
  femaleBF_pc: number;
  deficit_pc: number;
  weeklyWeightLoss_pc: number;
  TEF: number;
  factorM: number;
}

export interface TrainingStatus {
  level: TrainingLevel;
  bulkingEBF: number;
  weeklyWeightGain_pc: number;
  anabolicMeals: number /** number of meals in the anabolic window */;
}

export enum AdjustmentAction {
  DecreaseIntakeByFullDeficit = 'DecreaseIntakeByFullDeficit',
  DecreaseIntakeByHalfDeficit = 'DecreaseIntakeByHalfDeficit',
  DecreaseIntakeByExcessGain = 'DecreaseIntakeByExcessGain',
  DoNothing = 'DoNothing',
  IncreaseIntakeByExcessLoss = 'IncreaseIntakeByExcessLoss',
  IncreaseIntakeByFullSurplus = 'IncreaseIntakeByFullSurplus',
  ManualOverride = 'ManualOverride',
}

export enum AdjustmentActionReason {
  TooMuchWeightLoss = 'TooMuchWeightLoss',
  TooMuchFatGain = 'TooMuchFatGain',
  NotEnoughFatLoss = 'NotEnoughFatLoss',
  NotEnoughWeightGain = 'NotEnoughWeightGain',
}

export interface SlopePoint {
  x: number;
  y: number;
}

/** which body-fat series the progress check read its fat trend from (#5456) */
export enum BodyFatSeries {
  /** the mean of the skinfold sites */
  Calipers = 'Calipers',
  /** the waist tape */
  Tape = 'Tape',
}

/**
 * One reading the progress check fitted, with the calendar day it was taken on (#5456).
 * `x` is a ROUNDED offset in days from the instant of the check, not a calendar day: a 07:00
 * weigh-in yesterday, checked at 22:00, has `x = -2`. A chart must place the reading by
 * `dateIso`, the entry's own local date. `y` is in kg (weight), mm (skinfolds) or cm (waist).
 */
export interface DatedSlopePoint extends SlopePoint {
  dateIso: string;
}

/**
 * What the solver's analysis stage learned from the user's recent weigh-ins and body-fat readings.
 * solve() and checkForPlanAdjustments() compute it once and hand it to every step that needs it,
 * so a step that reads these values can only be called by code that ran the analysis.
 * The sample lists are always set. Every estimate can genuinely be missing: no bodyweight from
 * any source, too few measurements for a slope, or no body-fat reading yet.
 */
export interface WeighInAnalysis {
  estimatedBW_kg?: number;
  weightSlope_kgwk?: number;
  fatnessSlopeBottom?: number;
  fatnessSlopeTop?: number;
  /** the series the two fat slopes above were read from; undefined when there are none (#5456) */
  fatnessSeries?: BodyFatSeries;
  weightSamples: DatedSlopePoint[];
  caliperSamples: DatedSlopePoint[];
  tapeSamples: DatedSlopePoint[];
  estimatedBF_pc?: number;
  bodyComposition?: BodyComposition;
}

export interface PlanUpdateInternals {
  gender?: Gender;
  bodyComposition?: BodyComposition;
  trainingStatus?: TrainingStatus;
  physicalActivityFactor?: number;
  energyBalanceFactor?: number;
  setsWeekly?: number;
  trainingDaysWeekly?: number;
  mealsDaily?: number;

  // estimates made from the weigh-ins

  /** Estimated weight to use for BMR calcuations etc - we use weight estimate as of 1 week ago, to make the diet less agressive. */
  estimatedBW_kg?: number;
  weightSlope_kgwk?: number; /// kilograms per week
  /** Bottom and Top of the confidence interval percentiles of the slope from tapes or calipers.
   * values are not important - only tested against <0 or >0 . That's why we don't care if it's tapes or calipers. */
  fatnessSlopeBottom?: number;
  fatnessSlopeTop?: number;

  estimatedBF_pc?: number;

  BMR?: number;
  restingDayEE?: number;
  resistanceTrainingEE?: number;
  minimumDailyAverageIntake?: number;

  trainingDayEE?: number;

  adjustmentAction?: AdjustmentAction;
  adjustmentActionReason?: AdjustmentActionReason;
  bulkMealsPerWeek?: number;
  cutMealsPerWeek?: number;

  weeklyTargets?: { [key: string]: number } /** by INFOODS tags */;
  cutMealTargets?: { [key: string]: number } /** by INFOODS tags */;
  bulkMealTargets?: { [key: string]: number } /** by INFOODS tags */;
  fixedMealTargets?: { [key: string]: number } /** by INFOODS tags - used when calorie cycling is not enabled */;

  // internal debug data from weighins
  caliperSamples?: SlopePoint[];
  tapeSamples?: SlopePoint[];
  weightSamples?: SlopePoint[];

  accumulatedBulkMealsCount?: number;
}
